const BASE_PRINT_STYLES = `
  @page {
    margin: 12mm;
  }

  * {
    box-shadow: none !important;
  }

  body {
    font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
    font-size: 13px;
    color: #0f172a;
    background: #fff;
    padding: 0;
    margin: 0;
  }

  h1, h2, h3 {
    margin: 0 0 8px 0;
    font-weight: 700;
    color: #0f172a;
  }

  h1 { font-size: 20px; }
  h2 { font-size: 16px; }
  h3 { font-size: 14px; }

  .print-meta {
    color: #475569;
    font-size: 12px;
    margin: 2px 0;
  }

  .print-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .print-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 8px;
    background: #f1f5f9;
    color: #0f172a;
    font-size: 12px;
  }

  .print-section {
    margin-bottom: 16px;
    page-break-inside: avoid;
  }

  .print-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .print-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
    font-size: 12px;
  }

  .print-table thead {
    background: #f8fafc;
  }

  .print-table th,
  .print-table td {
    border: 1px solid #e2e8f0;
    padding: 8px 10px;
    text-align: right;
  }

  .print-table th:first-child,
  .print-table td:first-child {
    text-align: left;
  }

  .print-table tr:nth-child(even) {
    background: #f8fafc;
  }

  .print-table tr {
    page-break-inside: avoid;
  }

  .print-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 8px;
  }

  .print-card {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px;
    background: #fff;
  }

  .print-card .label {
    color: #475569;
    font-size: 12px;
    margin-bottom: 4px;
  }

  .print-card .value {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }

  .print-note {
    font-size: 12px;
    color: #475569;
    margin-top: 8px;
  }

  .print-source {
    display: block;
  }
`;

interface PrintHtmlPayload {
  title: string;
  content: string;
  extraStyles?: string;
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const buildPrintHtml = ({ title, content, extraStyles }: PrintHtmlPayload) => `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>${BASE_PRINT_STYLES}${extraStyles || ''}</style>
  </head>
  <body>
    ${content}
  </body>
</html>
`;

const showPrintPrompt = (message: string) => {
  if (typeof document === 'undefined') return Promise.resolve(true);

  return new Promise<boolean>((resolve) => {
    const overlay = document.createElement('div');
    overlay.setAttribute('data-print-prompt', 'true');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.background = 'rgba(15, 23, 42, 0.45)';
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.zIndex = '9999';
    overlay.innerHTML = `
      <div style="
        background: #fff;
        color: #0f172a;
        border-radius: 12px;
        padding: 20px 24px;
        width: min(360px, 90vw);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
        font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
      ">
        <div style="font-size: 16px; font-weight: 700; margin-bottom: 8px;">Print request</div>
        <div style="font-size: 13px; color: #475569; line-height: 1.4;">${escapeHtml(message)}</div>
        <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px;">
          <button data-print-cancel style="
            padding: 8px 14px;
            border-radius: 8px;
            border: 1px solid #e2e8f0;
            background: #fff;
            color: #0f172a;
            font-weight: 600;
            cursor: pointer;
          ">Cancel</button>
          <button data-print-confirm style="
            padding: 8px 14px;
            border-radius: 8px;
            border: 1px solid #ea580c;
            background: #f97316;
            color: #fff;
            font-weight: 700;
            cursor: pointer;
          ">Print</button>
        </div>
      </div>
    `;

    const cleanup = (result: boolean) => {
      overlay.remove();
      resolve(result);
    };

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) cleanup(false);
    });

    overlay.querySelector('[data-print-cancel]')?.addEventListener('click', () => cleanup(false));
    overlay.querySelector('[data-print-confirm]')?.addEventListener('click', () => cleanup(true));

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cleanup(false);
      }
    };

    document.addEventListener('keydown', handleEscape, { once: true });
    document.body.appendChild(overlay);
  });
};

export const printHtml = async (payload: PrintHtmlPayload) => {
  const html = buildPrintHtml(payload);

  // Ask for explicit confirmation before triggering the browser print dialog
  const confirmed = await showPrintPrompt('This webpage is trying to print. Do you want to print this webpage?');
  if (!confirmed) return;

  if (typeof window !== 'undefined' && (window as any).electronAPI?.printReport) {
    await (window as any).electronAPI.printReport({ html, title: payload.title });
    return;
  }

  // Browser path: print through a hidden iframe
  const iframe = document.createElement('iframe');
  iframe.setAttribute('sandbox', 'allow-modals allow-same-origin');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';

  document.body.appendChild(iframe);

  const frameDoc = iframe.contentWindow?.document;
  if (frameDoc) {
    frameDoc.open();
    frameDoc.write(html);
    frameDoc.close();

    // Give the browser a tick to render before printing
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => iframe.remove(), 1000);
    }, 50);
  } else {
    iframe.remove();
  }
};
